import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../../../../components/common/Card/Card';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import ExerciseThumbnailImage from '../../../../components/common/ExerciseThumbnailImage/ExerciseThumbnailImage';
import Icon from '../../../../components/common/Icon/Icon';
import KebabMenu from '../../../../components/common/KebabMenu/KebabMenu';
import Checkbox from '../../../../components/common/Checkbox/Checkbox';
import ExerciseCardTopBar from './ExerciseCardTopBar';
import type { ExerciseItem } from '../../../../types/exercise';
import { TRAINING_EXERCISE_GROUPING_COLORS, TRAINING_EXERCISE_GROUPING_LABELS, isTabataSupportedTrainingGroupingType, type TrainingBlock, type TrainingBlockExercise, type TrainingSummaryMetric } from '../../../../types/training';
import { getCanonicalTrainingExecutionMethodValue, isTabataTrainingExecutionMethod } from '../../../../utils/trainingExecutionMethods';
import { clearActiveTrainingDrag, dropZoneToDropMode, getActiveTrainingDrag, getTrainingDragData, isTrainingExerciseDragEvent, resolveExerciseDropZone, resolveReorderDropZone, setTrainingDragData, setTrainingDragPreview, type ExerciseDropZone } from '../../utils/trainingDragData';
import { beginTrainingDragAutoScroll, endTrainingDragAutoScroll, getLastTrainingDragPointer } from '../../utils/trainingDragAutoScroll';
import { CATALOG_INSERT_PREVIEW_ID, clusterContainsExerciseId, previewInsertAtPosition, previewReorderItems } from '../../utils/reorderTrainingExercises';
import { useHorizontalFlip } from '../../hooks/useHorizontalFlip';
import ExerciseSwapModal from '../ExerciseSwapModal';
import ExerciseVideoModal from '../ExerciseVideoModal';
import styles from './TrainingBlockEditor.module.css';

type ExerciseDropMode = 'insert' | 'insert-after' | 'conjugate';

type ActiveDropTarget =
  | { type: 'exercise'; itemId: string; zone: ExerciseDropZone }
  | { type: 'append' }
  | { type: 'empty' };

interface TrainingBlockEditorProps {
  block: TrainingBlock;
  summaryMetrics: TrainingSummaryMetric[];
  methodOptions: Array<{
    value: string;
    label: string;
    color?: string | null;
    textColor?: string | null;
  }>;
  selectedExercise: ExerciseItem | null;
  activeExerciseId: string | null;
  canRemoveBlock: boolean;
  canMoveBlockUp: boolean;
  canMoveBlockDown: boolean;
  onAddExercise: (blockId: string) => void;
  onOpenExerciseConfig: (blockId: string, exerciseId: string, anchorRect: DOMRect) => Promise<void>;
  onRemoveExercise: (blockId: string, exerciseId: string) => void;
  onReplaceExercise: (
    blockId: string,
    itemId: string,
    nextExercise: ExerciseItem,
    replaceInEntirePlan: boolean,
  ) => void;
  onDropCatalogExercise: (blockId: string, exercise: ExerciseItem, targetExerciseId?: string, dropMode?: ExerciseDropMode) => void;
  onMoveExerciseByDrag: (sourceBlockId: string, exerciseId: string, targetBlockId: string, targetExerciseId?: string, dropMode?: ExerciseDropMode) => void;
  onMoveBlockUp: (blockId: string) => void;
  onMoveBlockDown: (blockId: string) => void;
  onMoveBlockByDrag: (sourceBlockId: string, targetBlockId: string) => void;
  onMoveBlockToLabel: (blockId: string, targetLabel: string) => void;
  splitLabels: string[];
  onRemoveBlock: (blockId: string) => void;
  readOnly?: boolean;
  isMultiSelectMode?: boolean;
  isSelectionLocked?: boolean;
  selectedExerciseIds?: string[];
  onToggleExerciseSelection?: (blockId: string, exerciseId: string, anchorRect: DOMRect) => void;
}

const BLOCK_DRAG_DATA_KEY = 'application/x-portal-training-block-drag';

let activeBlockDragId: string | null = null;

const normalizeMethodLookupKey = (value?: string | null) => value
  ?.normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase() ?? '';

const formatMethodFallbackLabel = (methodName: string) => {
  const normalizedValue = methodName.trim();

  if (!normalizedValue) {
    return '';
  }

  if (['amrap', 'emom', 'hiit', 'rft', 'tabata'].includes(normalizedValue.toLowerCase())) {
    return normalizedValue.toUpperCase();
  }

  const titleizedValue = normalizedValue
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  return titleizedValue
    .replace(/^Bi Set$/i, 'Bi-set')
    .replace(/^Tri Set$/i, 'Tri-set')
    .replace(/^Drop Set$/i, 'Drop-set')
    .replace(/^Rest Pause$/i, 'Rest-pause')
    .replace(/^Multiple Set$/i, 'Série múltipla');
};

const isTabataGroupRoot = (item: TrainingBlockExercise, structureType: TrainingBlockExercise['groupingType'] | 'multiple_set') => (
  isTabataSupportedTrainingGroupingType(structureType)
  && (item.conjugatedExercises?.length ?? 0) > 0
  && isTabataTrainingExecutionMethod(item.execution.method)
);

const clusterKeyFor = (item: TrainingBlockExercise) => {
  const memberIds = [item.id, ...(item.conjugatedExercises ?? []).map((child) => child.id)].sort();
  return `cluster:${memberIds.join('-')}`;
};

const createCatalogInsertPreviewItem = (exercise: ExerciseItem): TrainingBlockExercise => ({
  id: CATALOG_INSERT_PREVIEW_ID,
  exerciseId: exercise.id,
  name: exercise.name,
  group: exercise.group,
  objective: exercise.objective,
  execution: {
    sets: null,
    repetitions: null,
    rest: null,
    method: '',
    details: [],
  },
  muscleGroups: [],
  groupingType: null,
  conjugatedExercises: [],
  hasExerciseDefaultsLoaded: false,
});

const TrainingBlockEditor: React.FC<TrainingBlockEditorProps> = ({
  block,
  summaryMetrics,
  methodOptions,
  selectedExercise,
  activeExerciseId,
  canRemoveBlock,
  canMoveBlockUp,
  canMoveBlockDown,
  onAddExercise,
  onOpenExerciseConfig,
  onRemoveExercise,
  onReplaceExercise,
  onDropCatalogExercise,
  onMoveExerciseByDrag,
  onMoveBlockUp,
  onMoveBlockDown,
  onMoveBlockByDrag,
  onMoveBlockToLabel,
  splitLabels,
  onRemoveBlock,
  readOnly = false,
  isMultiSelectMode = false,
  isSelectionLocked = false,
  selectedExerciseIds = [],
  onToggleExerciseSelection,
}) => {
  const hasItems = block.items.length > 0;
  const [activeDropTarget, setActiveDropTarget] = useState<ActiveDropTarget | null>(null);
  const [draggingExerciseId, setDraggingExerciseId] = useState<string | null>(null);
  const [isCatalogDragActive, setIsCatalogDragActive] = useState(false);
  const draggingExerciseIdRef = useRef<string | null>(null);
  const reorderPreviewRef = useRef<{ targetId: string; dropMode: ExerciseDropMode } | null>(null);
  const blockRootRef = useRef<HTMLDivElement | null>(null);
  const dropConsumedRef = useRef(false);
  const dropZonePointerRef = useRef({ itemId: '', x: 0, y: 0 });
  const [isBlockDropActive, setIsBlockDropActive] = useState(false);
  const [isBlockDragging, setIsBlockDragging] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [pendingExerciseRemoval, setPendingExerciseRemoval] = useState<{ id: string; name: string } | null>(null);
  const [videoExercise, setVideoExercise] = useState<{ catalogId: number; name: string } | null>(null);
  const [swapExercise, setSwapExercise] = useState<{ itemId: string; catalogId: number; name: string } | null>(null);
  const methodMetadataByKey = useMemo(
    () => methodOptions.reduce<Record<string, { label: string; color?: string | null; textColor?: string | null }>>((accumulator, option) => {
      const metadata = {
        label: option.label,
        color: option.color ?? null,
        textColor: option.textColor ?? null,
      };

      [option.value, option.label, getCanonicalTrainingExecutionMethodValue(option.value)].forEach((candidate) => {
        const normalizedCandidate = normalizeMethodLookupKey(candidate);

        if (normalizedCandidate) {
          accumulator[normalizedCandidate] = metadata;
        }
      });

      return accumulator;
    }, {}),
    [methodOptions]
  );
  const selectedExerciseIdSet = useMemo(() => new Set(selectedExerciseIds), [selectedExerciseIds]);
  const catalogPreviewItem = useMemo(() => {
    if (!isCatalogDragActive) {
      return null;
    }

    const activeDrag = getActiveTrainingDrag();

    if (activeDrag?.type !== 'catalog-exercise') {
      return null;
    }

    return createCatalogInsertPreviewItem(activeDrag.exercise);
  }, [isCatalogDragActive]);
  const previewItems = useMemo(() => {
    if (activeDropTarget?.type !== 'exercise' || activeDropTarget.zone === 'conjugate') {
      return null;
    }

    const placement = activeDropTarget.zone === 'after' ? 'after' : 'before';

    if (draggingExerciseId) {
      return previewReorderItems(
        block.items,
        draggingExerciseId,
        activeDropTarget.itemId,
        placement,
      );
    }

    if (!catalogPreviewItem) {
      return null;
    }

    return previewInsertAtPosition(
      block.items,
      catalogPreviewItem,
      activeDropTarget.itemId,
      placement,
    );
  }, [activeDropTarget, block.items, catalogPreviewItem, draggingExerciseId]);
  const displayItems = previewItems ?? block.items;
  const registerFlipNode = useHorizontalFlip(
    displayItems.map((item) => `${item.id}:${(item.conjugatedExercises ?? []).map((child) => child.id).join(',')}`).join('|'),
  );
  const activeDropTargetRef = useRef(activeDropTarget);
  activeDropTargetRef.current = activeDropTarget;

  useEffect(() => () => {
    endTrainingDragAutoScroll();
  }, []);

  const resetDropTarget = useCallback(() => {
    draggingExerciseIdRef.current = null;
    reorderPreviewRef.current = null;
    dropZonePointerRef.current = { itemId: '', x: 0, y: 0 };
    setActiveDropTarget(null);
    setDraggingExerciseId(null);
    setIsCatalogDragActive(false);
  }, []);

  useEffect(() => {
    const handleWindowDragEnd = () => {
      resetDropTarget();
      clearActiveTrainingDrag();
    };

    window.addEventListener('dragend', handleWindowDragEnd);
    return () => window.removeEventListener('dragend', handleWindowDragEnd);
  }, [resetDropTarget]);

  const handleExerciseSurfaceDragLeave = useCallback((event: React.DragEvent<HTMLElement>, itemId: string) => {
    if (draggingExerciseIdRef.current || isTrainingExerciseDragEvent(event)) {
      return;
    }

    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setActiveDropTarget((currentTarget) => (
      currentTarget?.type === 'exercise' && currentTarget.itemId === itemId
        ? null
        : currentTarget
    ));
  }, []);

  const handleFallbackDragLeave = useCallback((targetType: 'append' | 'empty') => {
    setActiveDropTarget((currentTarget) => (currentTarget?.type === targetType ? null : currentTarget));
  }, []);

  const handleExerciseDragStart = useCallback((event: React.DragEvent<HTMLButtonElement>, exerciseId: string) => {
    const previewSource = (event.currentTarget.closest(`.${styles.exerciseCard}`) as HTMLElement | null) ?? event.currentTarget;

    setTrainingDragPreview(event, previewSource);
    draggingExerciseIdRef.current = exerciseId;
    dropConsumedRef.current = false;
    dropZonePointerRef.current = { itemId: '', x: 0, y: 0 };
    setDraggingExerciseId(exerciseId);
    beginTrainingDragAutoScroll();
    setTrainingDragData(event, {
      type: 'block-exercise',
      blockId: block.id,
      exerciseId,
    });
  }, [block.id]);

  const handleExerciseDragEnd = useCallback((event: React.DragEvent<HTMLButtonElement>, exerciseId: string, exerciseName: string) => {
    const dropEffect = event.dataTransfer.dropEffect;
    const pointer = getLastTrainingDragPointer();
    const blockRect = blockRootRef.current?.getBoundingClientRect();
    const droppedInsideBlock = Boolean(
      blockRect
      && pointer.x >= blockRect.left
      && pointer.x <= blockRect.right
      && pointer.y >= blockRect.top
      && pointer.y <= blockRect.bottom
    );

    endTrainingDragAutoScroll();
    resetDropTarget();

    if (dropConsumedRef.current || droppedInsideBlock) {
      dropConsumedRef.current = false;
      return;
    }

    if (dropEffect === 'none') {
      setPendingExerciseRemoval({ id: exerciseId, name: exerciseName });
    }
  }, [resetDropTarget]);

  const resolveDropEffect = useCallback(() => 'move' as const, []);

  const handleExerciseSurfaceDragOver = useCallback((event: React.DragEvent<HTMLElement>, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = resolveDropEffect();

    const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
    const targetRect = event.currentTarget.getBoundingClientRect();

    if (draggingId === itemId) {
      return;
    }

    const currentZone = activeDropTargetRef.current?.type === 'exercise' && activeDropTargetRef.current.itemId === itemId
      ? activeDropTargetRef.current.zone
      : null;
    const pointerDeltaPx = currentZone
      ? Math.hypot(
        event.clientX - dropZonePointerRef.current.x,
        event.clientY - dropZonePointerRef.current.y,
      )
      : Number.POSITIVE_INFINITY;

    if (draggingId) {
      const clusterRoot = block.items.find((entry) => clusterContainsExerciseId(entry, itemId));
      const isSameCluster = Boolean(
        clusterRoot && clusterContainsExerciseId(clusterRoot, draggingId)
      );
      const zone = isSameCluster
        ? resolveReorderDropZone(event.clientX, targetRect, currentZone === 'conjugate' ? null : currentZone)
        : resolveExerciseDropZone(event.clientX, targetRect, undefined, currentZone, pointerDeltaPx);

      if (zone !== currentZone || dropZonePointerRef.current.itemId !== itemId) {
        dropZonePointerRef.current = { itemId, x: event.clientX, y: event.clientY };
      }

      if (zone === 'conjugate') {
        reorderPreviewRef.current = {
          targetId: itemId,
          dropMode: 'conjugate',
        };

        setActiveDropTarget((currentTarget) => {
          if (
            currentTarget?.type === 'exercise'
            && currentTarget.itemId === itemId
            && currentTarget.zone === 'conjugate'
          ) {
            return currentTarget;
          }

          return { type: 'exercise', itemId, zone: 'conjugate' };
        });
        return;
      }

      const preview = previewReorderItems(
        block.items,
        draggingId,
        itemId,
        zone === 'after' ? 'after' : 'before',
      );

      reorderPreviewRef.current = preview
        ? {
            targetId: itemId,
            dropMode: dropZoneToDropMode(zone),
          }
        : null;

      setActiveDropTarget((currentTarget) => {
        if (
          currentTarget?.type === 'exercise'
          && currentTarget.itemId === itemId
          && currentTarget.zone === zone
        ) {
          return currentTarget;
        }

        return { type: 'exercise', itemId, zone };
      });
      return;
    }

    if (getActiveTrainingDrag()?.type === 'catalog-exercise') {
      setIsCatalogDragActive(true);
    }

    const zone = resolveExerciseDropZone(event.clientX, targetRect, undefined, currentZone, pointerDeltaPx);

    if (zone !== currentZone || dropZonePointerRef.current.itemId !== itemId) {
      dropZonePointerRef.current = { itemId, x: event.clientX, y: event.clientY };
    }

    reorderPreviewRef.current = {
      targetId: itemId,
      dropMode: dropZoneToDropMode(zone),
    };

    setActiveDropTarget((currentTarget) => {
      if (
        currentTarget?.type === 'exercise'
        && currentTarget.itemId === itemId
        && currentTarget.zone === zone
      ) {
        return currentTarget;
      }

      return { type: 'exercise', itemId, zone };
    });
  }, [block.items, draggingExerciseId, resolveDropEffect]);

  const handleFallbackDragOver = useCallback((event: React.DragEvent<HTMLElement>, targetType: 'append' | 'empty') => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = resolveDropEffect();
    reorderPreviewRef.current = null;
    setActiveDropTarget((currentTarget) => (currentTarget?.type === targetType ? currentTarget : { type: targetType }));
  }, [resolveDropEffect]);

  const handleBlockContentDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (readOnly) {
      return;
    }

    if (Array.from(event.dataTransfer.types).includes(BLOCK_DRAG_DATA_KEY)) {
      return;
    }

    if (!draggingExerciseIdRef.current && !isTrainingExerciseDragEvent(event)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = resolveDropEffect();

    if (activeDropTargetRef.current?.type === 'exercise') {
      return;
    }

    reorderPreviewRef.current = null;
    setActiveDropTarget((currentTarget) => (currentTarget?.type === 'append' ? currentTarget : { type: 'append' }));
  }, [readOnly, resolveDropEffect]);

  const handleBlockContentDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setActiveDropTarget((currentTarget) => {
      if (currentTarget?.type === 'append' || currentTarget?.type === 'empty') {
        return null;
      }

      if (!draggingExerciseIdRef.current && currentTarget?.type === 'exercise') {
        return null;
      }

      return currentTarget;
    });

    if (!draggingExerciseIdRef.current) {
      reorderPreviewRef.current = null;
    }
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLElement>, targetExerciseId?: string, dropMode: ExerciseDropMode = 'insert') => {
    event.preventDefault();
    event.stopPropagation();
    dropConsumedRef.current = true;
    const payload = getTrainingDragData(event);
    const reorderPreview = reorderPreviewRef.current;
    const isAppendDrop = !targetExerciseId;
    resetDropTarget();

    if (!payload) {
      return;
    }

    if (reorderPreview) {
      if (payload.type === 'catalog-exercise') {
        onDropCatalogExercise(block.id, payload.exercise, reorderPreview.targetId, reorderPreview.dropMode);
        return;
      }

      onMoveExerciseByDrag(payload.blockId, payload.exerciseId, block.id, reorderPreview.targetId, reorderPreview.dropMode);
      return;
    }

    if (payload.type === 'catalog-exercise') {
      onDropCatalogExercise(block.id, payload.exercise, isAppendDrop ? undefined : targetExerciseId, isAppendDrop ? 'insert' : dropMode);
      return;
    }

    onMoveExerciseByDrag(payload.blockId, payload.exerciseId, block.id, isAppendDrop ? undefined : targetExerciseId, isAppendDrop ? 'insert' : dropMode);
  }, [block.id, onDropCatalogExercise, onMoveExerciseByDrag, resetDropTarget]);

  const handleExerciseSurfaceDrop = useCallback((event: React.DragEvent<HTMLElement>, itemId: string) => {
    const preview = reorderPreviewRef.current;

    if (preview) {
      handleDrop(event, preview.targetId, preview.dropMode);
      return;
    }

    if (draggingExerciseIdRef.current) {
      event.preventDefault();
      event.stopPropagation();
      dropConsumedRef.current = true;
      resetDropTarget();
      return;
    }

    const zone = resolveExerciseDropZone(event.clientX, event.currentTarget.getBoundingClientRect());
    handleDrop(event, itemId, dropZoneToDropMode(zone));
  }, [handleDrop, resetDropTarget]);

  const handleMoveBlock = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.shiftKey && canMoveBlockDown) {
      onMoveBlockDown(block.id);
      return;
    }

    if (canMoveBlockUp) {
      onMoveBlockUp(block.id);
      return;
    }

    if (canMoveBlockDown) {
      onMoveBlockDown(block.id);
    }
  }, [block.id, canMoveBlockDown, canMoveBlockUp, onMoveBlockDown, onMoveBlockUp]);

  const handleMoveBlockContextMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (canMoveBlockDown) {
      onMoveBlockDown(block.id);
      return;
    }

    if (canMoveBlockUp) {
      onMoveBlockUp(block.id);
    }
  }, [block.id, canMoveBlockDown, canMoveBlockUp, onMoveBlockDown, onMoveBlockUp]);

  const handleBlockDragStart = useCallback((event: React.DragEvent<HTMLButtonElement>) => {
    const previewSource = (event.currentTarget.closest(`.${styles.blockMain}`) as HTMLElement | null) ?? event.currentTarget;

    activeBlockDragId = block.id;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(BLOCK_DRAG_DATA_KEY, block.id);
    event.dataTransfer.setData('text/plain', block.label);
    setTrainingDragPreview(event, previewSource);
    beginTrainingDragAutoScroll();
    setIsBlockDragging(true);
  }, [block.id, block.label]);

  const handleBlockDragEnd = useCallback(() => {
    activeBlockDragId = null;
    endTrainingDragAutoScroll();
    setIsBlockDragging(false);
    setIsBlockDropActive(false);
  }, []);

  const handleBlockDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
    const isBlockDrag = Boolean(activeBlockDragId)
      || Array.from(event.dataTransfer.types).includes(BLOCK_DRAG_DATA_KEY);

    if (!isBlockDrag || activeBlockDragId === block.id) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    setIsBlockDropActive(true);
  }, [block.id]);

  const handleBlockDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsBlockDropActive(false);
    }
  }, []);

  const handleBlockDrop = useCallback((event: React.DragEvent<HTMLElement>) => {
    const sourceBlockId = event.dataTransfer.getData(BLOCK_DRAG_DATA_KEY) || activeBlockDragId;
    setIsBlockDropActive(false);

    if (!sourceBlockId || sourceBlockId === block.id) {
      return;
    }

    event.preventDefault();
    onMoveBlockByDrag(sourceBlockId, block.id);
  }, [block.id, onMoveBlockByDrag]);

  const handleRemoveBlock = useCallback(() => {
    if (!canRemoveBlock) {
      return;
    }

    setIsRemoveModalOpen(true);
  }, [canRemoveBlock]);

  const handleCancelRemoveBlock = useCallback(() => {
    setIsRemoveModalOpen(false);
  }, []);

  const handleConfirmRemoveBlock = useCallback(() => {
    setIsRemoveModalOpen(false);
    onRemoveBlock(block.id);
  }, [block.id, onRemoveBlock]);

  const handleCancelRemoveExercise = useCallback(() => {
    setPendingExerciseRemoval(null);
  }, []);

  const handleConfirmRemoveExercise = useCallback(() => {
    if (!pendingExerciseRemoval) {
      return;
    }

    onRemoveExercise(block.id, pendingExerciseRemoval.id);
    setPendingExerciseRemoval(null);
  }, [block.id, onRemoveExercise, pendingExerciseRemoval]);

  const handleOpenExerciseVideo = useCallback((catalogId: number, name: string) => {
    setVideoExercise({ catalogId, name });
  }, []);

  const handleOpenExerciseSwap = useCallback((itemId: string, catalogId: number, name: string) => {
    setSwapExercise({ itemId, catalogId, name });
  }, []);

  const handleConfirmExerciseSwap = useCallback((nextExercise: ExerciseItem, replaceInEntirePlan: boolean) => {
    if (!swapExercise) {
      return;
    }

    onReplaceExercise(block.id, swapExercise.itemId, nextExercise, replaceInEntirePlan);
    setSwapExercise(null);
  }, [block.id, onReplaceExercise, swapExercise]);

  const renderExerciseCard = useCallback((
    item: TrainingBlockExercise,
    compact = false,
    tabataState?: { label?: string; tone: 'controller' | 'synced' },
  ) => {
    const methodLookupKey = normalizeMethodLookupKey(item.execution.method);
    const methodMetadata = methodMetadataByKey[methodLookupKey]
      ?? methodMetadataByKey[getCanonicalTrainingExecutionMethodValue(item.execution.method)];
    const methodLabel = methodMetadata?.label ?? formatMethodFallbackLabel(item.execution.method);
    const methodColor = methodMetadata?.color ?? undefined;
    const methodTextColor = methodMetadata?.textColor ?? undefined;
    const hasMethodTopBar = methodLabel.length > 0;
    const isTabataController = tabataState?.tone === 'controller';
    const isTabataSynced = tabataState?.tone === 'synced';
    const isMultiSelected = selectedExerciseIdSet.has(item.id);
    const isCatalogInsertPreview = item.id === CATALOG_INSERT_PREVIEW_ID;
    const showSelectionCheckbox = !readOnly && !isCatalogInsertPreview && (isMultiSelectMode || isMultiSelected);

    return (
      <Card
        key={item.id}
        className={`${styles.exerciseCard}${compact ? ` ${styles.exerciseCardCompact}` : ''}${activeExerciseId === item.id ? ` ${styles.exerciseCardActive}` : ''}${draggingExerciseId === item.id || isCatalogInsertPreview ? ` ${styles.exerciseCardDropPreview}` : ''}${isTabataController ? ` ${styles.exerciseCardTabataController}` : ''}${isTabataSynced ? ` ${styles.exerciseCardTabataSynced}` : ''}${isMultiSelected ? ` ${styles.exerciseCardMultiSelected}` : ''}`}
      >
        {showSelectionCheckbox ? (
          <div
            className={styles.exerciseSelectBox}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              if (isSelectionLocked || !isMultiSelectMode) {
                return;
              }

              onToggleExerciseSelection?.(block.id, item.id, event.currentTarget.getBoundingClientRect());
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Checkbox
              variant="classic"
              compact
              checked={isMultiSelected}
              ariaLabel={`Selecionar ${item.name}`}
              onPress={() => undefined}
            />
          </div>
        ) : null}
        <button
          type="button"
          className={`${styles.exerciseButton}${draggingExerciseId === item.id || isCatalogInsertPreview ? ` ${styles.exerciseButtonDragging}` : ''}`}
          onClick={(event) => {
            if (readOnly || isCatalogInsertPreview) {
              return;
            }

            if (isSelectionLocked) {
              event.preventDefault();
              return;
            }

            const rect = event.currentTarget.getBoundingClientRect();

            if (event.ctrlKey || event.metaKey || isMultiSelectMode) {
              event.preventDefault();
              onToggleExerciseSelection?.(block.id, item.id, rect);
              return;
            }

            void onOpenExerciseConfig(block.id, item.id, rect);
          }}
          draggable={!readOnly && !isCatalogInsertPreview && !isMultiSelectMode && !isSelectionLocked}
          onDragStart={readOnly || isCatalogInsertPreview || isMultiSelectMode || isSelectionLocked ? undefined : (event) => {
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault();
              return;
            }

            handleExerciseDragStart(event, item.id);
          }}
          onDragEnd={readOnly || isCatalogInsertPreview || isMultiSelectMode || isSelectionLocked ? undefined : (event) => handleExerciseDragEnd(event, item.id, item.name)}
        >
          {hasMethodTopBar ? <ExerciseCardTopBar label={methodLabel} color={methodColor} textColor={methodTextColor} /> : null}

          <div className={`${styles.thumbnailFrame}${compact ? ` ${styles.thumbnailFrameCompact}` : ''}${hasMethodTopBar ? ` ${styles.thumbnailFrameWithTopBar}` : ''}`}>
            <ExerciseThumbnailImage
              exerciseId={item.exerciseId}
              exerciseName={item.name}
              alt={`Thumbnail do exercicio ${item.name}`}
              className={styles.thumbnail}
            />
            <div className={styles.thumbnailOverlay}>
              {tabataState?.label ? (
                <span className={`${styles.exerciseStatusBadge}${isTabataController ? ` ${styles.exerciseStatusBadgeController}` : ` ${styles.exerciseStatusBadgeSynced}`}`}>
                  {tabataState.label}
                </span>
              ) : null}
              <span className={styles.exerciseName} title={item.name}>{item.name}</span>
            </div>
          </div>
        </button>

        {!readOnly && !isCatalogInsertPreview ? (
          <KebabMenu
            portal
            className={styles.exerciseKebab}
            triggerClassName={styles.exerciseKebabTrigger}
            items={[
              {
                icon: 'play_circle',
                label: 'Ver vídeo',
                onClick: () => handleOpenExerciseVideo(item.exerciseId, item.name),
              },
              {
                icon: 'change_circle',
                label: 'Trocar exercício',
                onClick: () => handleOpenExerciseSwap(item.id, item.exerciseId, item.name),
              },
              {
                icon: 'delete',
                label: 'Remover exercício',
                variant: 'danger',
                onClick: () => setPendingExerciseRemoval({ id: item.id, name: item.name }),
              },
            ]}
          />
        ) : null}
      </Card>
    );
  }, [activeExerciseId, block.id, draggingExerciseId, handleExerciseDragEnd, handleExerciseDragStart, handleOpenExerciseSwap, handleOpenExerciseVideo, isMultiSelectMode, isSelectionLocked, methodMetadataByKey, onOpenExerciseConfig, onToggleExerciseSelection, readOnly, selectedExerciseIdSet]);

  const renderExerciseEntry = useCallback((item: TrainingBlockExercise) => {
    const isCatalogInsertPreview = item.id === CATALOG_INSERT_PREVIEW_ID;

    if (isCatalogInsertPreview) {
      return (
        <div key={item.id} className={styles.exerciseEntry}>
          <div className={styles.dropSlot} aria-hidden="true" />
          <div
            className={styles.exerciseDropSurface}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
              event.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(event) => handleExerciseSurfaceDrop(event, item.id)}
          >
            {renderExerciseCard(item)}
          </div>
          <div className={styles.dropSlot} aria-hidden="true" />
        </div>
      );
    }

    const conjugatedExercises = item.conjugatedExercises ?? [];
    const hasConjugates = conjugatedExercises.length > 0;
    const groupedExercises = [item, ...conjugatedExercises];
    const structureType = item.groupingType ?? 'multiple_set';
    const structureLabel = TRAINING_EXERCISE_GROUPING_LABELS[structureType] ?? TRAINING_EXERCISE_GROUPING_LABELS.multiple_set;
    const structureBackgroundColor = TRAINING_EXERCISE_GROUPING_COLORS[structureType];
    const isTabataGroup = isTabataGroupRoot(item, structureType);
    const isDraggingWithinGroup = Boolean(
      draggingExerciseId && clusterContainsExerciseId(item, draggingExerciseId)
    );
    const dropTarget = activeDropTarget?.type === 'exercise' && activeDropTarget.itemId === item.id && !isDraggingWithinGroup
      ? activeDropTarget
      : null;
    const isConjugateTarget = dropTarget?.zone === 'conjugate';
    const entryKey = hasConjugates ? clusterKeyFor(item) : item.id;

    return (
      <div key={entryKey} className={styles.exerciseEntry}>
        <div className={styles.dropSlot} aria-hidden="true" />

        {hasConjugates ? (
          <div
            className={`${styles.conjugateGroup}${isConjugateTarget ? ` ${styles.conjugateGroupDropActive}` : ''}${isTabataGroup ? ` ${styles.conjugateGroupTabata}` : ''}`}
            style={structureBackgroundColor ? { backgroundColor: structureBackgroundColor } : undefined}
            onDragOver={(event) => {
              const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
              if (draggingId && clusterContainsExerciseId(item, draggingId)) {
                event.preventDefault();
                event.stopPropagation();
                event.dataTransfer.dropEffect = 'move';
                return;
              }

              handleExerciseSurfaceDragOver(event, item.id);
            }}
            onDragLeave={(event) => {
              const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
              if (draggingId && clusterContainsExerciseId(item, draggingId)) {
                return;
              }

              handleExerciseSurfaceDragLeave(event, item.id);
            }}
            onDrop={(event) => {
              const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
              if (draggingId && clusterContainsExerciseId(item, draggingId)) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }

              handleExerciseSurfaceDrop(event, item.id);
            }}
            title={isTabataGroup ? `${structureLabel} com Tabata sincronizado` : structureLabel}
          >
            {groupedExercises.map((groupedExercise) => (
                <div
                  key={groupedExercise.id}
                  ref={registerFlipNode(groupedExercise.id)}
                  className={styles.groupedExerciseDropSurface}
                  onDragOver={(event) => {
                    const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
                    if (!draggingId || !clusterContainsExerciseId(item, draggingId)) {
                      return;
                    }

                    handleExerciseSurfaceDragOver(event, groupedExercise.id);
                  }}
                  onDragLeave={(event) => {
                    const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
                    if (!draggingId || !clusterContainsExerciseId(item, draggingId)) {
                      return;
                    }

                    handleExerciseSurfaceDragLeave(event, groupedExercise.id);
                  }}
                  onDrop={(event) => {
                    const draggingId = draggingExerciseIdRef.current ?? draggingExerciseId;
                    if (!draggingId || !clusterContainsExerciseId(item, draggingId)) {
                      return;
                    }

                    handleExerciseSurfaceDrop(event, groupedExercise.id);
                  }}
                >
                  {renderExerciseCard(
                    groupedExercise,
                    true,
                    isTabataGroup
                      ? { tone: groupedExercise.id === item.id ? 'controller' : 'synced' }
                      : undefined,
                  )}
                </div>
            ))}
            {isConjugateTarget ? (
              <div
                className={`${styles.dropZoneOverlay} ${styles.dropZoneOverlayConjugate}`}
                aria-hidden="true"
              />
            ) : null}
          </div>
        ) : (
          <div
            ref={registerFlipNode(item.id)}
            className={`${styles.exerciseDropSurface}${isConjugateTarget ? ` ${styles.exerciseDropSurfaceActive}` : ''}`}
            onDragOver={(event) => handleExerciseSurfaceDragOver(event, item.id)}
            onDragLeave={(event) => handleExerciseSurfaceDragLeave(event, item.id)}
            onDrop={(event) => handleExerciseSurfaceDrop(event, item.id)}
          >
            {renderExerciseCard(item)}
            {isConjugateTarget ? (
              <div
                className={`${styles.dropZoneOverlay} ${styles.dropZoneOverlayConjugate}`}
                aria-hidden="true"
              />
            ) : null}
          </div>
        )}

        <div className={styles.dropSlot} aria-hidden="true" />
      </div>
    );
  }, [activeDropTarget, draggingExerciseId, handleExerciseSurfaceDragLeave, handleExerciseSurfaceDragOver, handleExerciseSurfaceDrop, registerFlipNode, renderExerciseCard]);

  return (
    <>
      <div
        ref={blockRootRef}
        className={`${styles.block}${isBlockDropActive ? ` ${styles.blockDropActive}` : ''}${isBlockDragging ? ` ${styles.blockDragging}` : ''}`}
        onDragOver={handleBlockDragOver}
        onDragLeave={handleBlockDragLeave}
        onDrop={handleBlockDrop}
      >
        <div className={styles.blockLabel}>
          {!readOnly && splitLabels.length > 1 ? (
            <select
              className={styles.blockLabelSelect}
              value={block.label}
              onChange={(event) => onMoveBlockToLabel(block.id, event.target.value)}
              aria-label={`Mover split ${block.label} para outra posição`}
              title="Trocar a posição deste split"
            >
              {splitLabels.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          ) : (
            block.label
          )}
        </div>

        <div className={styles.blockMain}>
          <div className={styles.blockSummaryBar}>
            <div className={styles.blockSummaryMetrics}>
              {summaryMetrics.map((metric, index) => (
                <React.Fragment key={`${block.id}-${metric.label}`}>
                  {index > 0 ? <span className={styles.blockSummaryDivider} aria-hidden="true" /> : null}
                  <div className={styles.blockSummaryMetric}>
                    <span className={styles.blockSummaryMetricLabel}>{metric.label}:</span>
                    <span className={styles.blockSummaryMetricValue}>{metric.value}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            <button
              type="button"
              className={styles.blockSummaryRemoveButton}
              onClick={handleRemoveBlock}
              disabled={readOnly || !canRemoveBlock}
              aria-label={`Excluir split ${block.label}`}
              title={canRemoveBlock ? 'Excluir split' : 'O treino precisa de pelo menos um bloco'}
            >
              <Icon name="delete" size={12} className={styles.blockSummaryRemoveIcon} />
              <span>Excluir split</span>
            </button>
          </div>

          <div
            className={styles.blockContent}
            onDragOver={readOnly ? undefined : handleBlockContentDragOver}
            onDragLeave={readOnly ? undefined : handleBlockContentDragLeave}
            onDrop={readOnly ? undefined : ((event) => handleDrop(event))}
          >
            {hasItems ? (
                <div className={`${styles.exerciseScroller}${draggingExerciseId || isCatalogDragActive ? ` ${styles.exerciseScrollerDragging}` : ''}`}>
                <button
                  type="button"
                  className={styles.moveBlockButton}
                  onClick={handleMoveBlock}
                  onContextMenu={handleMoveBlockContextMenu}
                  draggable={!readOnly}
                  onDragStart={readOnly ? undefined : handleBlockDragStart}
                  onDragEnd={readOnly ? undefined : handleBlockDragEnd}
                  aria-label={`Mover bloco ${block.label}`}
                  title="Arraste para reorganizar o bloco. Clique move para cima; Shift+clique ou clique com botão direito move para baixo."
                  disabled={readOnly}
                >
                  <Icon name="drag_indicator" size={12} className={styles.dragIndicatorIcon} />
                </button>

                {displayItems.map(renderExerciseEntry)}

                {!readOnly && (
                  <button
                    type="button"
                    className={`${styles.addExerciseCard}${selectedExercise ? ` ${styles.addExerciseCardEnabled}` : ''}${activeDropTarget?.type === 'append' ? ` ${styles.addExerciseCardDropActive}` : ''}`}
                    onClick={() => onAddExercise(block.id)}
                    aria-label={selectedExercise ? `Adicionar ${selectedExercise.name}` : 'Selecione um exercicio'}
                    onDragOver={(event) => handleFallbackDragOver(event, 'append')}
                    onDragLeave={() => handleFallbackDragLeave('append')}
                    onDrop={(event) => handleDrop(event)}
                  >
                    <span className={styles.addExerciseIcon} aria-hidden="true" />
                  </button>
                )}
              </div>
            ) : (
              <div
                className={`${styles.emptyBlock}${activeDropTarget?.type === 'empty' ? ` ${styles.emptyBlockDropActive}` : ''}`}
                onDragOver={(event) => handleFallbackDragOver(event, 'empty')}
                onDragLeave={() => handleFallbackDragLeave('empty')}
                onDrop={(event) => handleDrop(event)}
              >
              {!readOnly && (
                <div className={styles.blockEntryActions}>
                  <button
                    type="button"
                    className={styles.moveBlockButton}
                    onClick={handleMoveBlock}
                    onContextMenu={handleMoveBlockContextMenu}
                    draggable
                    onDragStart={handleBlockDragStart}
                    onDragEnd={handleBlockDragEnd}
                    aria-label={`Mover bloco ${block.label}`}
                    title="Arraste para reorganizar o bloco. Clique move para cima; Shift+clique ou clique com botão direito move para baixo."
                  >
                    <Icon name="drag_indicator" size={20} className={styles.dragIndicatorIcon} />
                  </button>

                  <button
                    type="button"
                    className={`${styles.addExerciseCard}${selectedExercise ? ` ${styles.addExerciseCardEnabled}` : ''}`}
                    onClick={() => onAddExercise(block.id)}
                    aria-label={selectedExercise ? `Adicionar ${selectedExercise.name}` : 'Selecione um exercicio'}
                  >
                    <span className={styles.addExerciseIcon} aria-hidden="true" />
                  </button>
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmModal
        open={!readOnly && isRemoveModalOpen}
        title="Excluir split"
        message={`Deseja excluir o split ${block.label}?`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleConfirmRemoveBlock}
        onCancel={handleCancelRemoveBlock}
      />
      <ConfirmModal
        open={!readOnly && Boolean(pendingExerciseRemoval)}
        title="Remover exercício"
        message={pendingExerciseRemoval ? `Deseja remover ${pendingExerciseRemoval.name} do bloco ${block.label}?` : ''}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleConfirmRemoveExercise}
        onCancel={handleCancelRemoveExercise}
      />
      <ExerciseVideoModal
        open={Boolean(videoExercise)}
        exerciseId={videoExercise?.catalogId ?? null}
        exerciseName={videoExercise?.name ?? ''}
        onClose={() => setVideoExercise(null)}
      />
      <ExerciseSwapModal
        open={Boolean(swapExercise)}
        currentExerciseId={swapExercise?.catalogId ?? null}
        currentExerciseName={swapExercise?.name ?? ''}
        onCancel={() => setSwapExercise(null)}
        onConfirm={handleConfirmExerciseSwap}
      />
    </>
  );
};

export default TrainingBlockEditor;